<template>
  <!-- 우측 그룹 네비 — 서버(/api/core/nav)에서 권한 필터된 그룹/메뉴 트리 -->
  <nav v-show="visible" class="app-sidenav">
    <div v-if="serverNav.length === 0" class="app-sidenav__empty">
      메뉴가 없습니다
    </div>

    <div v-for="group in serverNav" :key="group.code" class="app-sidenav__group">
      <button
        type="button"
        class="app-sidenav__group-title"
        @click="toggleGroup(group.code)"
      >
        <span
          class="material-symbols-outlined app-sidenav__chevron"
          :class="{ 'is-collapsed': isCollapsed(group.code) }"
        >expand_more</span>
        <span class="app-sidenav__group-label">{{ label(group.name) }}</span>
      </button>
      <div v-show="!isCollapsed(group.code)" class="app-sidenav__items">
        <a
          v-for="item in group.menus"
          :key="item.code"
          class="app-sidenav__item"
          @click="go(item)"
        >
          {{ label(item.name) }}
        </a>
      </div>
    </div>
  </nav>
</template>

<script>
import axios from "axios";

const COLLAPSE_KEY = "sidenav.collapsed";

export default {
  name: "AppSideNav",
  props: {
    visible: { type: Boolean, default: false },
  },
  data() {
    return {
      // 그룹 code → true(접힘) 맵. 새로고침해도 유지되도록 localStorage 에 저장.
      collapsed: this.loadCollapsed(),
      // 서버에서 받은 nav 트리 [{ code, name, menus:[{ code, name, path, action, isDefault }] }]
      serverNav: [],
    };
  },
  computed: {
    // 로그인 상태 변화 시 nav 재조회용
    isLogin() {
      return this.$store.getters.isLogin;
    },
  },
  watch: {
    // 재로그인 시 nav 재조회 (요청 정책: 재로그인/재조회)
    isLogin(v) {
      if (v) this.loadNav();
      else this.serverNav = [];
    },
  },
  async mounted() {
    await this.loadNav();
  },
  methods: {
    // 서버에서 권한 필터된 메뉴 트리 조회 (/api/core/nav)
    async loadNav() {
      try {
        const { data } = await axios.get("/api/core/nav");
        this.serverNav = Array.isArray(data) ? data : [];
      } catch (e) {
        this.serverNav = [];
      }
    },
    loadCollapsed() {
      try {
        return JSON.parse(localStorage.getItem(COLLAPSE_KEY)) || {};
      } catch {
        return {};
      }
    },
    isCollapsed(code) {
      return !!this.collapsed[code];
    },
    toggleGroup(code) {
      // 반응성 보장을 위해 새 객체로 교체 후 저장
      this.collapsed = { ...this.collapsed, [code]: !this.collapsed[code] };
      try {
        localStorage.setItem(COLLAPSE_KEY, JSON.stringify(this.collapsed));
      } catch {
        /* noop */
      }
    },
    // 메뉴/그룹 라벨: i18n 키가 있으면 번역, 없으면 원문 그대로(다른 모듈의 하드코딩 한글은 그대로 노출)
    label(name) {
      return this.$te(name) ? this.$t(name) : name;
    },
    // 메뉴 항목: action 있으면 전역 모달 열기, 아니면 라우팅
    go(item) {
      if (item.action) {
        this.$store.dispatch("openModal", item.action);
      } else if (item.path) {
        this.$router.push(item.path).catch(() => {});
      }
      this.$emit("navigate");
    },
  },
};
</script>

<style scoped>
.app-sidenav {
  width: 260px;
  height: 100%;
  padding: 16px 2px;
  /* 라이트=흰색(base), 다크=기존 surface 유지 */
  background: rgb(var(--c-base));
  border-left: 1px solid rgb(var(--c-line));
  box-sizing: border-box;
  /* 메뉴가 많거나 창 높이가 작아 넘칠 때 스크롤 (스크롤바 UI 는 숨김) */
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
}
.app-sidenav::-webkit-scrollbar {
  display: none; /* Chrome/Safari/Edge */
}
html.dark .app-sidenav {
  background: rgb(var(--c-surface));
}
.app-sidenav__empty {
  color: rgb(var(--c-muted));
  font-size: 14px;
  padding: 8px;
}
.app-sidenav__group {
  margin-bottom: 8px;
}
/* 그룹(모듈) 타이틀 = 접기/펼치기 토글 버튼 */
.app-sidenav__group-title {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 6px 6px;
  background: transparent;
  border: none;
  color: rgb(var(--c-content));
  font-weight: 500;
  font-size: 15px;
  text-align: left;
  cursor: pointer;
  border-radius: 6px;
}
.app-sidenav__group-title:hover {
  background: rgb(var(--c-content) / 0.08);
}
.app-sidenav__group-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* 화살표: 펼침=아래(▼), 접힘=오른쪽(▶) 회전 */
.app-sidenav__chevron {
  font-size: 20px;
  line-height: 1;
  color: rgb(var(--c-muted));
  transition: transform 0.15s ease;
}
.app-sidenav__chevron.is-collapsed {
  transform: rotate(-90deg);
}
/* 하위 메뉴 — 모듈 텍스트 시작 위치(30px)에서 +10px 우측(40px)으로 들여쓰기.
   세로 가이드선은 border 대신 ::before 로 그려, 첫 메뉴 행 중앙 ~ 마지막 메뉴 행 중앙까지만 표시(트리 커넥터). */
.app-sidenav__items {
  position: relative;
  margin-left: 40px;
  padding-left: 2px;
}
.app-sidenav__items::before {
  content: "";
  position: absolute;
  left: 0;
  top: 16px;    /* 첫 메뉴 행 중앙 (item 높이 ≈ 33px 의 절반) */
  bottom: 16px; /* 마지막 메뉴 행 중앙 */
  width: 1px;
  background: rgb(var(--c-line));
}
.app-sidenav__item {
  display: block;
  padding: 8px 12px;
  color: rgb(var(--c-content));
  font-size: 14px;
  border-radius: 6px;
  cursor: pointer;
}
.app-sidenav__item:hover {
  background: rgb(var(--c-content) / 0.08);
}
</style>
